import WorkoutCancelButton from "@/components/fitness/WorkoutCancelButton";
import MirrorBackButton from "@/components/MirrorBackButton";
import { useFitnessResource } from "@/hooks/useFitnessResource";
import { readFitnessHub } from "@/lib/fitnessData";
import { getWorkoutDraft } from "@/lib/fitnessDraft";
import FitnessDataState from "@/components/fitness/FitnessDataState";
import WorkoutDraftPersistenceStatus from "@/components/fitness/WorkoutDraftPersistenceStatus";
import {
  ensureWorkoutDraftHydrated, getFitnessDraftPersistenceStatus,
  retryWorkoutDraftPersistence, subscribeFitnessDraftPersistence,
} from "@/lib/fitnessDraftPersistence";
import React, { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import {
  Dumbbell, ListChecks,
  TrendingUp, Clock, Ruler,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { InfoTooltip } from "@/components/InfoTooltip";
import FitnessMiniMirror from "@/components/fitness/FitnessMiniMirror";
import WearableSources from "@/components/sleep/WearableSources";
import { usePremium } from "@/contexts/PremiumContext";
import { analytics } from "@/lib/analytics";

const SB_FITNESS = "https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/Modulo%20Fittness/";

// Fitness core structure (fitness-core-overhaul): 4 cards — Planos, Progresso,
// Histórico, Medidas. Removed: "Treino de Hoje" (pure duplicate of the hero,
// zero exclusive function — same navigate("/fitness/workout")), "Biblioteca"
// and "Mapa Corporal" (no cards; routes stay reachable — see PR report).
const SUB_MODULES = [
  { icon: ListChecks, name: "fitness.plans", path: "/fitness/guided", bg: `${SB_FITNESS}Academia%20-%20Planos.jpg`, gradient: "linear-gradient(to top, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.14) 55%, rgba(0,0,0,0.0) 100%)", filter: "brightness(1.6) saturate(1.2)" },
  { icon: TrendingUp, name: "fitness.myProgress", path: "/fitness/progress", bg: `${SB_FITNESS}Academia%20-%20Progresso.jpg`, gradient: "linear-gradient(to top, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.14) 55%, rgba(0,0,0,0.0) 100%)", filter: "brightness(1.6) saturate(1.2)" },
  { icon: Clock, name: "fitness.history", path: "/fitness/history", bg: `${SB_FITNESS}Academia%20-%20Historico.jpg`, gradient: "linear-gradient(to top, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.14) 55%, rgba(0,0,0,0.0) 100%)", filter: "brightness(1.6) saturate(1.2)" },
  // Medidas (Body Scan) — reuses the body-map cover until a dedicated
  // "Academia - Body Scan.jpg" is uploaded to the Modulo Fittness bucket.
  { icon: Ruler, name: "fitness.bodyScanCard", path: "/fitness/body-scan", bg: `${SB_FITNESS}Academia%20-%20Mapa%20corporal.jpg`, gradient: "linear-gradient(to top, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.14) 55%, rgba(0,0,0,0.0) 100%)", filter: "brightness(1.6) saturate(1.2)" },
];

/* ─── Ambient floating particles (cyan/purple, slow drift) ─── */
const PARTICLE_CONFIGS = [
  { w: 2, color: "#00e5ff", opacity: 0.17, left: "12%", top: "18%", yDrift: -15, xDrift: 6, dur: 10, delay: 0 },
  { w: 1.5, color: "hsl(265 60% 65%)", opacity: 0.15, left: "78%", top: "30%", yDrift: -12, xDrift: -5, dur: 12, delay: 1.5 },
  { w: 2.5, color: "#00e5ff", opacity: 0.18, left: "45%", top: "55%", yDrift: -18, xDrift: 4, dur: 9, delay: 0.8 },
  { w: 1.8, color: "hsl(265 60% 65%)", opacity: 0.16, left: "28%", top: "72%", yDrift: -14, xDrift: -7, dur: 11, delay: 2.5 },
  { w: 2, color: "#00e5ff", opacity: 0.19, left: "65%", top: "42%", yDrift: -16, xDrift: 5, dur: 13, delay: 1 },
  { w: 1.6, color: "hsl(265 60% 65%)", opacity: 0.15, left: "88%", top: "65%", yDrift: -13, xDrift: -4, dur: 11.5, delay: 2 },
];

const FitnessParticles = React.memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
    {PARTICLE_CONFIGS.map((p, i) => (
      <motion.div key={i} className="absolute rounded-full"
        style={{ width: p.w, height: p.w, background: p.color, opacity: p.opacity, left: p.left, top: p.top }}
        animate={{ y: [0, p.yDrift, 0], x: [0, p.xDrift, 0], opacity: [p.opacity, p.opacity + 0.04, p.opacity] }}
        transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut", delay: p.delay }} />
    ))}
  </div>
));
FitnessParticles.displayName = "FitnessParticles";

const FitnessBreathingOrbs = React.memo(() => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
    <motion.div
      className="absolute -top-20 -right-20 w-[280px] h-[280px] rounded-full"
      style={{ background: "radial-gradient(circle, rgba(0,229,255,0.04) 0%, transparent 70%)" }}
      animate={{ opacity: [0.3, 0.6, 0.3], scale: [0.95, 1.05, 0.95] }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute -bottom-32 -left-24 w-[250px] h-[250px] rounded-full"
      style={{ background: "radial-gradient(circle, rgba(139,92,246,0.03) 0%, transparent 70%)" }}
      animate={{ opacity: [0.2, 0.5, 0.2], scale: [1, 1.08, 1] }}
      transition={{ duration: 10, delay: 2, repeat: Infinity, ease: "easeInOut" }}
    />
  </div>
));
FitnessBreathingOrbs.displayName = "FitnessBreathingOrbs";

const Fitness: React.FC = () => {
  const navigate = useNavigate();
  const { t, tf } = useLanguage();
  const reducedMotion = useReducedMotion();
  const resource = useFitnessResource(readFitnessHub);
  const ownerId = resource.userId;
  const captureDraftOwner = resource.capture;
  const getDraftPersistenceSnapshot = useCallback(() => getFitnessDraftPersistenceStatus(ownerId), [ownerId]);
  const persistence = useSyncExternalStore(subscribeFitnessDraftPersistence, getDraftPersistenceSnapshot, getDraftPersistenceSnapshot);
  const [hydration, setHydration] = useState<{ ownerId: string; state: "checking" | "ready" | "failed" } | null>(null);
  const [retryingOwner, setRetryingOwner] = useState<string | null>(null);

  useEffect(() => {
    setRetryingOwner(null);
    if (!ownerId) { setHydration(null); return; }
    let active = true;
    const capturedOwnerIsCurrent = captureDraftOwner();
    const isCurrent = () => active && capturedOwnerIsCurrent();
    setHydration({ ownerId, state: "checking" });
    void ensureWorkoutDraftHydrated(ownerId, isCurrent).then(() => {
      if (isCurrent()) setHydration({ ownerId, state: "ready" });
    }).catch(() => {
      if (isCurrent()) setHydration({ ownerId, state: "failed" });
    });
    return () => { active = false; };
  }, [ownerId, captureDraftOwner]);

  // A cold session may have a durable checkpoint even with empty session storage.
  // Only the current owner's completed hydration can establish that it is absent.
  const draftHydrated = !!ownerId && hydration?.ownerId === ownerId && hydration.state === "ready";
  const draft = draftHydrated && ownerId ? getWorkoutDraft(ownerId) : null;
  const retryingDraft = !!ownerId && retryingOwner === ownerId;
  const draftStatus: Pick<ReturnType<typeof getFitnessDraftPersistenceStatus>, "status" | "operation"> =
    draftHydrated || persistence.status === "failed" ? persistence
      : hydration?.ownerId === ownerId && hydration.state === "failed"
        ? { status: "failed", operation: "hydrate" }
        : { status: "hydrating", operation: "hydrate" };
  const retryDraft = async () => {
    if (!ownerId || retryingDraft) return;
    const isCurrent = captureDraftOwner();
    setRetryingOwner(ownerId);
    try {
      await retryWorkoutDraftPersistence(ownerId, isCurrent);
      await ensureWorkoutDraftHydrated(ownerId, isCurrent);
      if (isCurrent()) setHydration({ ownerId, state: "ready" });
    } catch {
      if (isCurrent() && !draftHydrated) setHydration({ ownerId, state: "failed" });
    } finally {
      if (isCurrent()) setRetryingOwner(null);
    }
  };

  const todayWorkout = resource.data?.nextWorkout;
  const score = resource.data?.sleepScore;
  const sleepWarning = typeof score === "number" ? score < 50 ? t("fitness.sleepWarning.rest") : score < 60 ? t("fitness.sleepWarning.light") : null : null;
  const goal = resource.data?.profile?.days_per_week;

  const stagger = (i: number) => ({ delay: 0.06 + i * 0.07, duration: 0.5 });

  // Instrumentation (módulo liberado no Free): open-by-plan. Fires once the
  // subscription tier resolves (skips the loading flash).
  const { plan, isLoading: planLoading } = usePremium();
  useEffect(() => {
    if (planLoading) return;
    analytics.moduleOpenTier("fitness", plan);
  }, [planLoading, plan]);

  useEffect(() => { if (resource.status === "ready" && !resource.data?.profile) navigate("/fitness/onboarding", { replace: true }); }, [resource.status, resource.data, navigate]);
  if (resource.status !== "ready") return <MobileLayout className="pb-28 pt-8"><MirrorBackButton /><FitnessDataState status={resource.status} busy={resource.isRefreshing} onRetry={resource.refresh} /></MobileLayout>;

  return (
    <MobileLayout className="pb-28 pt-8 relative overflow-hidden">
      {!reducedMotion && <><FitnessParticles /><FitnessBreathingOrbs /></>}

      <MirrorBackButton className="mb-4" />

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5" style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("fitness.subtitle")}</p>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">{t("fitness.title")}</h1>
            <InfoTooltip title={t("tooltips.fitness.title")} description={t("tooltips.fitness.description")} />
          </div>
        </div>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "hsl(187 72% 50% / 0.10)", border: "1px solid hsl(187 72% 50% / 0.20)" }}>
          <Dumbbell size={16} style={{ color: "hsl(187 72% 50%)" }} />
        </div>
      </motion.div>

      {/* ── FITNESS MINI-MIRROR W6 (DNA match: Agenda PR6 + Nutrition v3) ──
          Self-contained component: frame TV avatar | weekly volume hero |
          3 chips with Drawer drill-down | voice button. RPC-driven via
          useFitnessSnapshot inside the component. */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(0)}
        className="mb-4 relative z-10">
        <FitnessMiniMirror />
      </motion.div>

      {/* SLEEP WARNING */}
      {sleepWarning && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={stagger(0.5)}
          className="rounded-2xl p-3.5 mb-4 flex items-center gap-3"
          style={{ background: "hsl(35 90% 50% / 0.08)", border: "1px solid hsl(35 90% 50% / 0.25)" }}>
          <span className="text-[16px]">😴</span>
          <p className="text-[11px] font-body" style={{ color: "hsl(35 90% 60%)" }}>{sleepWarning}</p>
        </motion.div>
      )}

      {/* TODAY'S WORKOUT — CTA-first card.
          Title + exercise-preview pills intentionally removed; users see the
          exercise list on the /fitness/workout screen after tapping the CTA.
          todayWorkout state is kept because the fetch query still drives the
          "no active plan" empty state and downstream logic elsewhere. */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(1)}
        aria-busy={draftStatus.status === "hydrating" || retryingDraft}
        className="rounded-2xl p-4 mb-4" style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.18)" }}>
        <WorkoutDraftPersistenceStatus snapshot={draftStatus} onRetry={() => void retryDraft()} busy={retryingDraft} className="mb-3" />
        <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-2" style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("fitness.ux.nextWorkout")}</p>
        {draft && <><button type="button" onClick={() => navigate("/fitness/workout")} className="w-full mb-3 p-4 text-left rounded-xl border border-primary/50 bg-primary/10">
          <span className="block font-semibold text-primary">{t("fitness.ux.resumeWorkout")}</span>
          <span className="block text-sm text-muted-foreground mt-1">{draft.workoutName} · {t("fitness.ux.draftOnDevice")}</span>
        </button><WorkoutCancelButton ownerId={resource.userId!} onDiscard={() => navigate("/fitness/free-workout")} className="mb-3 w-full" /></>}
        {todayWorkout && <p className="font-semibold mb-3">{todayWorkout.name}</p>}
        {draftHydrated && !todayWorkout && (
          <p className="text-[12px] font-body text-foreground/50 mb-3">{t("fitness.plans.noActivePlan")}</p>
        )}
        <button type="button" disabled={!draftHydrated} onClick={() => navigate("/fitness/free-workout")}
          className="w-full py-4 rounded-xl font-display text-[14px] font-bold tracking-wide active:scale-[0.97] disabled:opacity-50"
          style={{ background: "hsl(var(--primary))", color: "hsl(var(--background))" }}>
          {t("fitness.freeWorkout.btn")}
        </button>
        <button type="button" disabled={!draftHydrated} onClick={() => navigate("/fitness/guided")}
          className="w-full py-3 rounded-xl font-display text-[13px] font-semibold tracking-wide active:scale-[0.97] mt-2 disabled:opacity-50"
          style={{ background: "transparent", color: "hsl(187 72% 55%)", border: "1px solid hsl(187 72% 50% / 0.4)" }}>
          {t("fitness.workoutPlan")}
        </button>
      </motion.div>

      <section className="rounded-2xl border border-primary/20 bg-card p-4 mb-4" aria-label={t("fitness.ux.lastSevenDays")}>
        <p className="text-sm text-muted-foreground">{t("fitness.ux.lastSevenDays")}</p>
        <p className="text-xl font-semibold mt-1">{tf("fitness.ux.trainingDays", { count: String(resource.data?.trainingDays ?? 0) })}</p>
        {Number.isInteger(goal) && goal > 0 && goal <= 7 && <>
          <p className="text-sm text-muted-foreground mt-1">{tf("fitness.ux.chosenGoal", { count: String(goal) })}</p>
          <progress className="w-full h-2 mt-3 accent-primary" max={goal} value={Math.min(resource.data?.trainingDays ?? 0, goal)} aria-label={t("fitness.ux.lastSevenDays")} />
        </>}
        <button type="button" onClick={() => navigate("/fitness/progress")} className="min-h-11 text-primary text-sm mt-1">{t("fitness.myProgress")} →</button>
      </section>

      {/* STREAK card standalone — REMOVED Fase Fitness Mini-Mirror.
          Streak + weekly volume now live in the scorecard inside the
          Mini-Mirror card hero above. */}

      {/* SUB-MODULES GRID */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(3)}>
        <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-3" style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("fitness.explore")}</p>
        <div className="grid grid-cols-2 gap-2.5 mb-4">
          {SUB_MODULES.map(mod => (
            <button type="button" key={mod.name} disabled={!draftHydrated && mod.path === "/fitness/guided"} onClick={() => navigate(mod.path)}
              className="disabled:opacity-50 flex flex-col items-center justify-end p-3 rounded-2xl active:scale-[0.96] hover:scale-[1.02] hover:brightness-110 transition-all text-center relative overflow-hidden"
              style={{ minHeight: 120, background: `${mod.gradient}, url(${mod.bg}) center/cover no-repeat`, border: "1px solid rgba(0,229,255,0.28)", filter: mod.filter }}>
              <mod.icon size={20} className="mb-1.5 relative z-10" style={{ color: "hsl(187 72% 60%)", filter: "drop-shadow(0 1px 3px rgba(0,229,255,0.3))" }} />
              <p className="font-display text-[11px] font-bold text-white leading-snug relative z-10" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>{t(mod.name)}</p>
            </button>
          ))}
        </div>
      </motion.div>

      {/* WEARABLE — o MESMO card-verdade do Sleep, não um irmão divergente.
          O card estático que morava aqui dizia "em breve" para uma integração
          no ar desde julho, prometia "Apple Watch" (a fonte é o HealthKit) e
          não levava a lugar nenhum. WearableSources lê user_connections e
          resolve web/nativo sozinho. */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(4)}>
        <WearableSources />
      </motion.div>

    </MobileLayout>
  );
};

export default Fitness;
